/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - restockNotifyMe (New)
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.nativeSelector = document.getElementById(`variants-${this.section.dataset.section}`);
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateOnSale();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      
      // NEW: Listen for clicks to handle "Notify Me" action
      if (this.productForm) {
        this.productForm.addEventListener('click', this.handleFormClick.bind(this));
      }

      this.applySearchParams();
      this.setAttribute('loaded', '');
    }

    /**
     * Handles clicks on the product form (specifically the Notify Me button)
     */
    handleFormClick(evt) {
      if (this.addBtn && (evt.target === this.addBtn || this.addBtn.contains(evt.target))) {
        if (this.addBtn.dataset.notifyMode === 'true') {
          evt.preventDefault();
          evt.stopImmediatePropagation();

          const modal = document.getElementById('RestockModal'); 

          if (modal) {
            // --- FIX 1: COMMUNICATION ---
            // Set display to flex to trigger the MutationObserver in the HTML script
            modal.style.display = 'flex'; 
            modal.setAttribute('aria-hidden', 'false');
            
            // Update Tags
            const infoInput = modal.querySelector('.restock-variant-info');
            
            if (infoInput && this.variant) {
               // 1. Get Product Title
               let productTitle = document.querySelector('.product-title') 
                 ? document.querySelector('.product-title').innerText 
                 : (document.title || 'Product');
               
               // 2. Get Variant Title
               let variantTitle = this.variant.title;

               // --- FIX 2: SANITIZE TAGS ---
               // Replace commas with spaces to prevent Shopify from splitting the tag
               productTitle = productTitle.replace(/,/g, ' ');
               variantTitle = variantTitle.replace(/,/g, ' ');
                 
               const humanTag = `Restock Request: ${productTitle} ${variantTitle}`;
               const idTag = `Restock Request: ${this.variant.id}`;
               
               infoInput.value = `newsletter, Restock Request, ${humanTag}, ${idTag}`;
            }
          }
        }
      }
    }

    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updateOnSale();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      this.updateProductLabel();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          allVariants: this.data.variants,
          selectedOptions: this.selectedOptions
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and status.
     * UPDATED FOR TRANSLATIONS
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      
      const variantExists = !!this.variant;
      const variantAvailable = this.variant && this.variant.available;
      
      // Use window.theme.strings for translations
      const unavailableStr = (window.theme && window.theme.strings.unavailable) || "Unavailable";
      // Fallback to English if translation key is missing
      const notifyMeStr = (window.theme && window.theme.strings.restockNotifyMe) || "NOTIFY ME"; 

      if (variantExists && !variantAvailable) {
        // CASE: Sold Out -> Show Translated "Notify Me"
        this.addBtn.textContent = notifyMeStr; 
        this.addBtn.disabled = false;
        this.addBtn.type = 'button';
        this.addBtn.dataset.notifyMode = 'true';
      } else if (variantAvailable) {
        // CASE: Available
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        this.addBtn.disabled = false;
        this.addBtn.type = 'submit';
        delete this.addBtn.dataset.notifyMode;
      } else {
        // CASE: Invalid
        this.addBtn.textContent = unavailableStr;
        this.addBtn.disabled = true;
        this.addBtn.type = 'submit';
        delete this.addBtn.dataset.notifyMode;
      }
    }

    // --- HELPER METHODS ---

    static updateOptionAvailability(optionEl, exists, available, formatted = null) {
      const el = optionEl;
      const unavailableText = exists ? 'Out of stock' : 'Unavailable'; 
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        let badgeText = '';
        let badgeColor = '';

        // Priority 1: Out of stock / Unavailable
        if (!available) {
          badgeText = unavailableText;
          badgeColor = '#C8102E';
        // In-stock availability checks
        } else if (formatted) {
          // Priority 2: 1 Left (tracked inventory with 1 item remaining)
          if (formatted.inventory_management && formatted.inventory_quantity === 1) {
            badgeText = '1 Left';
          // Priority 3: Only a few left (tracked inventory with 2 or 3 remaining)
          } else if (formatted.inventory_management && formatted.inventory_quantity > 1 && formatted.inventory_quantity <= 3) {
            badgeText = 'Only a few left';
          }
        }

        const em = el.querySelector('em');
        if (badgeText) {
          if (em) {
            em.textContent = badgeText;
            em.style.color = badgeColor;
            em.hidden = false;
          } else {
            el.insertAdjacentHTML('beforeend', ` <em class="pointer-events-none"${badgeColor ? ` style="color: ${badgeColor};"` : ''}>${badgeText}</em>`);
          }
        } else if (em) {
          em.hidden = true;
          em.style.color = '';
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMode } = this.dataset; 
      let currVariant = this.variant;
      if (!this.variant) currVariant = { options: this.selectedOptions };

      if (availabilityMode === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;
            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });
            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });
              if (optionEl) {
                const formatted = this.data.formatted ? this.data.formatted[variant.id] : null;
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available, formatted);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            let variantsExist = false;
            let variantsAvailable = false;
            let matchedVariant = null;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                  matchedVariant = v;
                }
              }
            });
            const formatted = matchedVariant && this.data.formatted ? this.data.formatted[matchedVariant.id] : null;
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable, formatted);
          });
        });
      }
    }

    updateOnSale() {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        selector.querySelectorAll('.js-option').forEach(optionEl => {
          const labelEl = optionEl.nextElementSibling;
          if (!labelEl) return;
          const isSwatch = labelEl.classList.contains('opt-label--swatch') || labelEl.classList.contains('opt-label--image');
          if (!isSwatch) return;
          const existing = labelEl.querySelector('.sale-dot');
          if (existing) existing.remove();
          const value = selector.dataset.selectorType === 'dropdown' ? optionEl.dataset.value : optionEl.value;
          const hasSale = this.data.variants.some(v =>
            v.compare_at_price > v.price &&
            v.options[selectorIndex] === value &&
            this.selectedOptions.every((sel, idx) =>
              idx === selectorIndex || sel === null || v.options[idx] === sel
            )
          );
          if (hasSale) {
            const dot = document.createElement('span');
            dot.className = 'sale-dot';
            labelEl.appendChild(dot);
          }
        });
      });
    }

    updateProductLabel() {
      const labelElement = this.section.querySelector('.product-label');
      if (!labelElement) return;
      if (this.variant) {
        const metafields = window.variantMetafields ? window.variantMetafields[this.variant.id] : null;
        const label = metafields ? metafields.label : '';
        if (label) {
          labelElement.textContent = label;
          labelElement.style.display = 'inline';
        } else {
          labelElement.style.display = 'none';
        }
      } else {
        labelElement.style.display = 'none';
      }
    }

    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;
      let hideBackorder = true;
      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];
        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;
          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }
      this.backorder.hidden = hideBackorder;
    }

    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;
      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;
      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;
      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-price .price');
      if (!this.price) return;
      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price) variant = this.data.variants[i];
        }
      }
      if (variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;
          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }
        this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        this.price.classList.toggle('price--sold-out', !variant.available && !this.preSelection);
      }
      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = !this.preSelection;
    }

    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;
      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    updateBarcode() {
      this.barcode = this.barcode || this.section.querySelector('.product-barcode__value');
      if (!this.barcode) return;
      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form, .js-instalments-form');
      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    getSelectedOptions() {
      const selectedOptions = [];
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });
      return selectedOptions;
    }

    getSelectedVariant() {
      return this.data.variants.find(
        (v) => v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);
      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;
        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${value}"]`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${value}"]`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}